import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { useCapabilities } from '@/hooks/useCapabilities';
import type { MeCapabilitiesDto, ModuleStateDto } from '@/hooks/useCapabilities';

/**
 * Story 121.10 — TENANT_ADMIN self-serve module storefront.
 *
 * Lists the tenant's modules (from `/me/capabilities`) and lets the
 * tenant admin activate/deactivate entitled modules or subscribe to
 * purchasable ones. Dependency-graph errors and entitlement gaps
 * surface inline with the problem-detail returned by the backend.
 *
 * Lives at `/admin/modules` (the tenant counterpart of the
 * platform-admin storefront at `/admin/platform-modules` from 121.9).
 */
export function ModuleStorefrontPage() {
  const { t } = useTranslation();
  const { states, loading } = useCapabilities();
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  // Local override so toggles are visible immediately before /me/capabilities
  // refetch; reset whenever a write succeeds.
  const [override, setOverride] = useState<ModuleStateDto[] | null>(null);

  const view: ModuleStateDto[] = override ?? states;

  const callAction = async (key: string, action: 'activate' | 'deactivate' | 'subscribe') => {
    setError(null);
    setBusyKey(key);
    try {
      const { data } = await apiClient.post<MeCapabilitiesDto>(`/me/modules/${key}/${action}`);
      setOverride(data.states);
    } catch (e) {
      const r = (e as { response?: { data?: { detail?: string; kind?: string } } }).response?.data;
      setError(
        r?.detail ?? t('modules.errors.actionFailed', { defaultValue: 'Action failed' }),
      );
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">
        {t('modules.tenant.title', { defaultValue: 'Modules' })}
      </h1>
      <p className="text-sm text-muted-foreground">
        {t('modules.tenant.intro', {
          defaultValue:
            'Turn modules your tenant is licensed for on or off. Subscribe to additional purchasable modules to extend Kairos.',
        })}
      </p>

      {error && (
        <div
          data-testid="tenant-modules-error"
          className="rounded border border-red-700 bg-red-950/40 p-3 text-sm text-red-200"
        >
          {error}
        </div>
      )}

      {loading && view.length === 0 && (
        <div className="text-sm text-muted-foreground">
          {t('modules.tenant.loading', { defaultValue: 'Loading modules…' })}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {view.map((s) => (
          <TenantModuleCard
            key={s.key}
            state={s}
            busy={busyKey === s.key}
            onActivate={() => callAction(s.key, 'activate')}
            onDeactivate={() => callAction(s.key, 'deactivate')}
            onSubscribe={() => callAction(s.key, 'subscribe')}
          />
        ))}
      </div>
    </div>
  );
}

function TenantModuleCard({
  state,
  busy,
  onActivate,
  onDeactivate,
  onSubscribe,
}: {
  state: ModuleStateDto;
  busy: boolean;
  onActivate: () => void;
  onDeactivate: () => void;
  onSubscribe: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      data-testid={`tenant-modules-card-${state.key}`}
      className="rounded-md border border-sidebar-border bg-card p-4"
    >
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-sm font-semibold">
            {t(`module.${state.key}.name`, { defaultValue: state.name })}
          </div>
          <div className="text-xs text-muted-foreground">{state.key}</div>
        </div>
        <div className="text-xs uppercase text-muted-foreground">{state.tierFloor}</div>
      </div>
      {t(`module.${state.key}.description`, { defaultValue: '' }) && (
        <p className="mt-1 text-xs text-muted-foreground">
          {t(`module.${state.key}.description`, { defaultValue: '' })}
        </p>
      )}
      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
        <span>
          {t('modules.col.entitled', { defaultValue: 'Entitled' })}: {state.entitled ? '✓' : '—'}
        </span>
        <span>·</span>
        <span>
          {t('modules.col.activated', { defaultValue: 'Activated' })}:{' '}
          {state.activated ? '✓' : '—'}
        </span>
      </div>
      <div className="mt-3 flex gap-2">
        {state.foundation ? (
          <span className="text-xs text-muted-foreground">
            {t('modules.foundation', { defaultValue: 'foundation — always on' })}
          </span>
        ) : !state.entitled ? (
          state.purchasable ? (
            <Button
              size="sm"
              disabled={busy}
              data-testid={`tenant-modules-subscribe-${state.key}`}
              onClick={onSubscribe}
            >
              {t('modules.tenant.subscribe', { defaultValue: 'Subscribe' })}
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">
              {t('modules.tenant.notEntitled', { defaultValue: 'Not entitled — ask Lopettia' })}
            </span>
          )
        ) : state.activated ? (
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            data-testid={`tenant-modules-deactivate-${state.key}`}
            onClick={onDeactivate}
          >
            {t('modules.tenant.deactivate', { defaultValue: 'Disable' })}
          </Button>
        ) : (
          <Button
            size="sm"
            disabled={busy}
            data-testid={`tenant-modules-activate-${state.key}`}
            onClick={onActivate}
          >
            {t('modules.tenant.activate', { defaultValue: 'Enable' })}
          </Button>
        )}
      </div>
    </div>
  );
}
